import { useAstriaCopy } from "../astria/copy";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { localizeBrand } from "../../utils/brandTerms";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { api } from "../../services/api";
import { useChat } from "../../contexts/ChatContext";
import { useCategories } from "../../contexts/CategoryContext";
import BirthDetailsPopup from "../../utils/BirthDetailsPopup";
import LifeGPSCard from "./LifeGPSCard";
import EnergyMatchPromoCard from "./EnergyMatchPromoCard";
import LifeRingsSection from "./rings/LifeRingsSection";
import { LifeGPSContext, LifeGPSData } from "./types";
import { axisLabel, stateLabel } from "./axisMeta";
import i18n from "../../i18n";
import { getAppLanguage, toLocale, useAppLanguage } from "../../utils/appLanguage";
import { serverErrorMessage } from "../../utils/serverError";

const SCHEMA_VERSION = 4;

const formatUpdated = (iso: string) => {
  const d = new Date(iso);
  const locale = toLocale(getAppLanguage());
  const time = d.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
  return d.toDateString() === new Date().toDateString()
    ? i18n.t("lifeGps.page.updatedToday", { time })
    : i18n.t("lifeGps.page.updatedOn", {
        date: d.toLocaleDateString(locale, { month: "short", day: "numeric" }),
        time,
      });
};

const readUser = (): any | null => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const hasBirthDate = (user: any) =>
  Boolean(
    String(
      user?.dob || user?.date_of_birth || user?.dateOfBirth || user?.birthDate || "",
    ).trim(),
  );

const Shell: React.FC<{ children: React.ReactNode; center?: boolean }> = ({
  children,
  center,
}) => (
  <div
    className={`astria-dark w-full bg-astria-bg px-4 pt-6 pb-8 sm:px-6 sm:pt-8 md:px-8 ${
      center ? "flex min-h-[60vh] items-center justify-center" : ""
    }`}
  >
    {children}
  </div>
);

// Page: DOB gate → load saved map (or generate the first one) → render card.
const LifeGPS: React.FC = () => {
  const copy = useAstriaCopy();
  const { t } = useTranslation();
  const appLanguage = useAppLanguage();
  const userId = readUser()?._id ?? null;
  const [searchParams] = useSearchParams();
  const subCategoryId = searchParams.get("category");
  const navigate = useNavigate();
  const { startNewChat, setCurrentChat } = useChat();
  const { categories } = useCategories();

  const [tab, setTab] = useState<"areas" | "direction">("areas");
  const [hasDOB, setHasDOB] = useState(() => hasBirthDate(readUser()));
  const [showBirthPopup, setShowBirthPopup] = useState(
    () => !hasBirthDate(readUser()),
  );
  const [data, setData] = useState<LifeGPSData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // True when new timing or new saved context exists since the last map.
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const generate = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.post("/backend/lifeGps/create", {
        userId,
        subCategoryId,
      });
      if (!res.data?.success || !res.data?.data) {
        throw new Error(res.data?.message || i18n.t("lifeGps.page.buildFailed"));
      }
      setData(res.data.data as LifeGPSData);
      setUpdateAvailable(Boolean(res.data.updateAvailable));
    } catch (err: any) {
      setError(serverErrorMessage(err, "lifeGps.page.buildFailed"));
    } finally {
      setIsLoading(false);
    }
  }, [userId, subCategoryId]);

  // Show the saved map instantly; only call the AI when there isn't one yet.
  useEffect(() => {
    if (tab !== "direction" || !hasDOB || !userId) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await api.get(`/backend/lifeGps/latest/${userId}`);
        if (cancelled) return;
        const saved = res.data?.data;
        // A map written in another language is rebuilt in the current one.
        if (
          saved?.schemaVersion === SCHEMA_VERSION &&
          (!saved.language || saved.language === appLanguage)
        ) {
          setData(res.data.data as LifeGPSData);
          setUpdateAvailable(Boolean(res.data.updateAvailable));
          setIsLoading(false);
          return;
        }
      } catch {
        // Fall through to generating a fresh map.
      }
      if (!cancelled) await generate();
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, hasDOB, userId, generate, appLanguage]);

  // "Ask Astria": open a talk chat with the composer pre-filled with this
  // Life GPS so the user only has to finish the question.
  const handleAsk = async (ctx: LifeGPSContext, question?: string) => {
    const talkCategory =
      categories.find(
        (c) => c.name === "HealJai Talk" || c.name === "Astria Talk",
      ) || categories.find((c) => c.name === "Companion Talk");
    if (!talkCategory) {
      toast.error(t("lifeGps.page.chatUnavailable"));
      return;
    }
    try {
      const newChat = await startNewChat(talkCategory.id);
      setCurrentChat(newChat);
      const s = ctx.states;
      const prefill = t("lifeGps.page.prefill", {
        sentence: ctx.oneSentence,
        direction: stateLabel("direction", s.direction),
        pace: stateLabel("pace", s.pace),
        energy: stateLabel("energy", s.energy),
        focus: stateLabel("focus", s.focus),
        question: question ?? "",
      });
      navigate(`/sanctuary?chat=${newChat.id}`, {
        state: { lifeGpsPrefill: prefill },
      });
    } catch {
      toast.error(t("lifeGps.page.chatOpenFailed"));
    }
  };

  const tabs = (
    <>
    <div className="mx-auto mb-6 max-w-xl text-center">
      <h1 className="text-3xl font-light tracking-tight text-astria-text">{localizeBrand("Life GPS", i18n.language)}</h1>
      <p className="mt-2 text-astria-muted">{copy("direction")} {copy("pace")}</p>
      <button
        type="button"
        onClick={() => navigate("/astrology")}
        className="mt-3 text-sm text-astria-accent underline-offset-4 hover:underline"
      >
        {copy("astroViewCta")}
      </button>
    </div>
    <div
      role="tablist"
      aria-label={t("lifeGps.page.viewLabel")}
      className="mx-auto mb-5 flex w-fit rounded-full bg-slate-100 p-1"
    >
      {(
        [
          ["areas", t("lifeGps.page.tabAreas")],
          ["direction", axisLabel("direction")],
        ] as const
      ).map(([id, label]) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={tab === id}
          onClick={() => setTab(id)}
          className={`rounded-full px-5 py-1.5 text-sm font-semibold transition ${
            tab === id
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
    </>
  );

  if (tab === "areas") {
    return (
      <Shell>
        {tabs}
        <LifeRingsSection />
        <EnergyMatchPromoCard />
      </Shell>
    );
  }

  if (showBirthPopup && !data) {
    return (
      <Shell>
        {tabs}
        <BirthDetailsPopup
          isOpen={showBirthPopup}
          category="LifeGraph"
          onClose={() => setShowBirthPopup(false)}
          onSave={() => {
            setShowBirthPopup(false);
            setHasDOB(true);
          }}
        />
      </Shell>
    );
  }

  if (!hasDOB) {
    return (
      <Shell>
        {tabs}
        <div className="mx-auto max-w-2xl rounded-3xl border border-gray-200 bg-gray-50 p-8 text-center shadow-sm">
          <h2 className="mb-3 text-2xl font-semibold text-gray-900">
            {t("lifeGps.page.dobTitle")}
          </h2>
          <p className="mb-6 text-gray-600">
            {t("lifeGps.page.dobBody")}
          </p>
          <button
            type="button"
            className="rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-700"
            onClick={() => setShowBirthPopup(true)}
          >
            {t("lifeGps.page.dobCta")}
          </button>
        </div>
      </Shell>
    );
  }

  if (isLoading && !data) {
    return (
      <Shell>
        {tabs}
        <div className="mt-16 flex flex-col items-center text-center gap-4">
          <div className="relative h-16 w-16">
            <div className="absolute inset-0 rounded-full border-4 border-slate-200" />
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-indigo-500 border-r-sky-500 border-b-emerald-500 animate-spin" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700">
              {t("lifeGps.page.loadingTitle")}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {t("lifeGps.page.loadingBody")}
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  if (error && !data) {
    return (
      <Shell>
        {tabs}
        <div className="mx-auto max-w-2xl rounded-3xl border border-red-200 bg-white p-6 sm:p-8 text-center">
          <p className="text-red-500">{error}</p>
          <button
            type="button"
            className="mt-4 rounded-full bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-slate-700"
            onClick={generate}
          >
            {t("lifeGps.page.tryAgain")}
          </button>
        </div>
      </Shell>
    );
  }

  if (!data) return null;

  return (
    <Shell>
        {tabs}
      <div className="mx-auto max-w-3xl">
        <LifeGPSCard data={data} updating={isLoading} onAsk={handleAsk} />
        <div className="mt-4 text-center">
          <button
            type="button"
            disabled={isLoading || !updateAvailable}
            onClick={generate}
            className="rounded-full border border-slate-200 bg-white px-5 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            {isLoading ? t("lifeGps.page.updating") : t("lifeGps.page.update")}
          </button>
          <p className="mt-2 text-xs text-slate-500">
            {formatUpdated(data.generatedAt)}
            {!updateAvailable && !isLoading
              ? ` · ${t("lifeGps.page.upToDate")}`
              : ""}
          </p>
          {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
        </div>
      </div>
      <EnergyMatchPromoCard />
    </Shell>
  );
};

export default LifeGPS;
